"use client";

import { demoHrefForSource } from "@/lib/demo-links";

import Link from "next/link";
import posthog from "posthog-js";
import { useEffect, useMemo, useState } from "react";
import { ScrollArea } from "@ocr-monorepo/ui/components/scroll-area";
import { Button } from "@ocr-monorepo/ui/components/button";
import { CodeBlock } from "@/components/codeblock";
import { MossClosingCta } from "@/components/marketing/moss-closing-cta";

export type SolutionPageData = {
  slug: string;
  label: string;
  h1: string;
  description: string;
  primaryCta: string;
  videoTitle: string;
  videoDescription: string;
  videoSrc?: string;
  /** Optional still shown before the video decodes, avoiding an empty frame. */
  videoPoster?: string;
  transformation: string;
  fields: string[];
  schema: Array<{ name: string; columns: string[] }>;
  primaryTable: string;
  relationship: string;
  benefits: string[];
  edgeCases: string[];
  guides?: Array<{
    title: string;
    description: string;
    href: string;
  }>;
  faqs: Array<{ question: string; answer: string }>;
  finalCta: string;
};

function capture(
  event: string,
  data: SolutionPageData,
  extra: Record<string, string> = {},
) {
  posthog.capture(event, {
    page_slug: data.slug,
    solution_type: data.label,
    ...extra,
  });
}

function exampleValue(field: string) {
  const examples: Record<string, string> = {
    "Invoice number": "INV-2026-1048",
    "Vendor name": "Northstar Office Supply",
    "Vendor address": "48 Market Street, Austin, TX",
    "Customer name": "Acme Operations",
    "Invoice date": "2026-07-01",
    "Due date": "2026-07-31",
    Currency: "USD",
    "PO reference": "PO-48219",
    Subtotal: "1,240.00",
    Tax: "99.20",
    Discounts: "25.00",
    Total: "1,314.20",
    "Payment terms": "Net 30",
    "Line-item description": "Archive storage service",
    Quantity: "12",
    "Unit price": "100.00",
    "Line total": "1,200.00",
    Institution: "Example National Bank",
    "Account holder": "Acme Operations",
    "Masked identifier": "•••• 4812",
    "Statement period": "2026-06-01 — 2026-06-30",
    "Opening balance": "18,402.15",
    "Closing balance": "21,873.44",
    "Transaction date": "2026-06-14",
    "Posting date": "2026-06-15",
    Description: "Cloud hosting subscription",
    Reference: "ACH-849201",
    Debit: "249.00",
    Credit: "",
    "Running balance": "21,873.44",
    "PO number": "PO-48219",
    Supplier: "Northstar Office Supply",
    "Supplier address": "48 Market Street, Austin, TX",
    Buyer: "Acme Procurement",
    "Order date": "2026-07-01",
    "Delivery date": "2026-07-14",
    "Shipping address": "400 Congress Ave, Austin, TX",
    "Billing address": "PO Box 902, Austin, TX",
    "Shipping terms": "FOB destination",
    SKU: "NS-ARCH-12",
    "Product description": "Archive storage service",
    Merchant: "Central Market",
    "Merchant address": "500 Elm Street, Austin, TX",
    "Transaction time": "14:32",
    "Receipt number": "RCPT-80491",
    "Payment method": "Corporate Visa •••• 4812",
    Tip: "18.00",
    Discount: "5.00",
    "Item description": "Team lunch",
    "Item total": "86.40",
    "Schema-defined records": "invoices, line_items",
    "Parent IDs": "invoice_id: inv_1048",
    "Child IDs": "line_item_id: li_001",
    "Normalized dates": "2026-07-01",
    "Decimal values": "1314.20",
    "Nullable fields": "purchase_order: null",
    "Source document IDs": "doc_abc123",
    "Extraction status": "completed",
    Vendor: "Northstar Office Supply",
    Category: "Operations",
    "Source ID": "doc_abc123",
  };
  const matchingKey = Object.keys(examples).find(
    (key) => key.toLowerCase() === field.toLowerCase(),
  );
  return matchingKey ? examples[matchingKey] : "—";
}

function tableId(name: string) {
  return `schema-${name}`;
}

const SAMPLE_ROW_COUNT = 8;

function relatedTable(column: string, tables: SolutionPageData["schema"]) {
  if (!column.endsWith("_id")) return undefined;
  const singular = column.slice(0, -3);
  return tables.find((table) => table.name === `${singular}s`);
}

function sampleCell(column: string, tableName: string, rowIndex: number) {
  const values: Record<string, string[]> = {
    vendor_id: ["ven_003", "ven_014", "ven_021"],
    invoice_id: ["inv_1048", "inv_1049", "inv_1050"],
    account_id: ["acc_4812", "acc_6190", "acc_7734"],
    statement_id: ["stm_0626", "stm_0526", "stm_0426"],
    supplier_id: ["sup_018", "sup_031", "sup_044"],
    purchase_order_id: ["po_48219", "po_48220", "po_48221"],
    merchant_id: ["mer_091", "mer_107", "mer_213"],
    receipt_id: ["rcp_80491", "rcp_80492", "rcp_80493"],
    name: ["Northstar Office Supply", "Acme Industrial", "Harbor Systems"],
    address: [
      "48 Market Street, Austin, TX",
      "214 King Road, Dallas, TX",
      "9 Harbor Avenue, Miami, FL",
    ],
    tax_identifier: [
      "US-TX-84-2910473",
      "US-TX-73-1846205",
      "US-FL-65-3901284",
    ],
    invoice_number: ["INV-2026-1048", "INV-2026-1049", "INV-2026-1050"],
    invoice_date: ["2026-07-01", "2026-07-03", "2026-07-08"],
    due_date: ["2026-07-31", "2026-08-02", "2026-08-07"],
    currency: ["USD", "USD", "EUR"],
    institution: [
      "Example National Bank",
      "Example National Bank",
      "Example National Bank",
    ],
    account_holder: ["Acme Operations", "Northstar Holdings", "Harbor Systems"],
    account_identifier: ["•••• 4812", "•••• 6190", "•••• 7734"],
    period_start: ["2026-06-01", "2026-05-01", "2026-04-01"],
    period_end: ["2026-06-30", "2026-05-31", "2026-04-30"],
    opening_balance: ["18,402.15", "16,991.02", "15,804.87"],
    closing_balance: ["21,873.44", "18,402.15", "16,991.02"],
    description: [
      "Archive storage service",
      "Document processing",
      "Usage overage",
    ],
    quantity: ["12", "4", "18"],
    unit_price: ["100.00", "249.00", "35.00"],
    line_total: ["1,200.00", "996.00", "630.00"],
    item_total: ["86.40", "42.18", "119.00"],
    transaction_date: ["2026-06-14", "2026-06-18", "2026-06-24"],
    posting_date: ["2026-06-15", "2026-06-19", "2026-06-25"],
    reference: ["ACH-849201", "CARD-184022", "WIRE-492810"],
    debit: ["249.00", "86.40", "630.00"],
    credit: ["—", "—", "1,500.00"],
    balance: ["21,873.44", "21,787.04", "22,657.04"],
    purchase_order_number: ["PO-48219", "PO-48220", "PO-48221"],
    order_date: ["2026-07-01", "2026-07-04", "2026-07-09"],
    expected_delivery_date: ["2026-07-14", "2026-07-18", "2026-07-22"],
    sku: ["NS-ARCH-12", "AC-DOC-04", "HS-USE-18"],
    transaction_time: ["14:32", "09:18", "17:04"],
    payment_method: [
      "Corporate Visa •••• 4812",
      "Mastercard •••• 6190",
      "Cash",
    ],
    subtotal: ["1,240.00", "942.00", "600.00"],
    tax: ["99.20", "75.36", "48.00"],
    total: ["1,314.20", "1,017.36", "648.00"],
  };
  if (column === "id") return `${tableName.slice(0, 3)}_${1048 + rowIndex}`;
  const samples = values[column];
  if (samples) return samples[rowIndex % samples.length];
  if (/(total|subtotal|tax|price|balance|amount)$/.test(column)) {
    return ["1,314.20", "996.00", "630.00"][rowIndex];
  }
  return exampleValue(
    column
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase()),
  );
}

function SampleDocument({
  data,
  rowIndex,
}: {
  data: SolutionPageData;
  rowIndex: number;
}) {
  const sourceTable =
    data.schema.find((table) => table.name === data.primaryTable) ??
    data.schema[0];
  const childTable = data.schema.find((table) =>
    table.columns.some(
      (column) => relatedTable(column, data.schema)?.name === sourceTable.name,
    ),
  );
  const sourceFields = sourceTable.columns.slice(0, 8).map((column) => ({
    field: column.replaceAll("_", " "),
    value: sampleCell(column, sourceTable.name, rowIndex),
  }));
  const detailFields = sourceFields.filter(
    ({ field }) => !/(total|subtotal|tax|balance|amount)$/.test(field),
  );
  const summaryFields = sourceFields.filter(({ field }) =>
    /(total|subtotal|tax|balance|amount)$/.test(field),
  );
  const lineFields = (childTable?.columns ?? []).slice(1, 6).map((column) => ({
    field: column.replaceAll("_", " "),
    value: sampleCell(column, childTable?.name ?? sourceTable.name, rowIndex),
  }));

  return (
    <aside className="min-w-0">
      <div className="flex items-end justify-between gap-4">
        <h3 className="text-xs font-medium uppercase tracking-[0.14em] text-primary">
          Sample document
        </h3>
        <p className="text-xs text-muted-foreground">
          Source PDF {String(rowIndex + 1).padStart(2, "0")} · Page 1
        </p>
      </div>
      <div className="mt-4 rounded-md border bg-card p-6 text-card-foreground shadow-sm sm:p-8">
        <div className="flex items-start justify-between gap-6 border-b pb-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              PDFParse sample {String(rowIndex + 1).padStart(2, "0")}
            </p>
            <p className="mt-2 text-2xl font-semibold uppercase tracking-[-0.02em]">
              {data.primaryTable.replaceAll("_", " ")}
            </p>
          </div>
          <div className="text-right font-mono text-[10px] uppercase leading-5 text-muted-foreground">
            <p>Digital document</p>
            <p>1 page</p>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 py-6">
          {detailFields.map(({ field, value }) => (
            <div key={field} className="min-w-0">
              <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                {field}
              </dt>
              <dd className="mt-1 truncate text-xs font-medium">{value}</dd>
            </div>
          ))}
        </dl>
        {lineFields.length > 0 && (
          <div className="border-t pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
              Document detail
            </p>
            <div className="mt-3 overflow-hidden rounded-sm border">
              {lineFields.slice(0, 4).map(({ field, value }, index) => (
                <div
                  key={field}
                  className={`flex items-center justify-between gap-4 px-3 py-2 text-[11px] ${index % 2 ? "bg-muted/60" : "bg-transparent"}`}
                >
                  <span className="text-muted-foreground">{field}</span>
                  <span className="font-mono font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {summaryFields.length > 0 && (
          <dl className="ml-auto mt-6 w-48 border-t pt-3">
            {summaryFields.map(({ field, value }) => (
              <div
                key={field}
                className="flex justify-between gap-4 py-1 text-[11px]"
              >
                <dt className="text-muted-foreground">{field}</dt>
                <dd className="font-mono font-medium">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className="mt-8 border-t pt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Sample data for product demonstration
        </div>
      </div>
    </aside>
  );
}

export function SolutionPage({
  data,
  highlightedApiExample,
}: {
  data: SolutionPageData;
  highlightedApiExample: string;
}) {
  const rootTable = useMemo(
    () =>
      data.schema.find((table) => table.name === data.primaryTable) ??
      data.schema[0],
    [data],
  );
  const [activeTableName, setActiveTableName] = useState(rootTable.name);
  const [tableHistory, setTableHistory] = useState<string[]>([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);

  const activeTable =
    data.schema.find((table) => table.name === activeTableName) ?? rootTable;
  const activeColumns = activeTable.columns.slice(0, 6);
  const childTables = data.schema.filter((candidate) =>
    candidate.columns.some(
      (column) => relatedTable(column, data.schema)?.name === activeTable.name,
    ),
  );
  const openTable = (tableName: string) => {
    if (tableName === activeTable.name) return;
    setTableHistory((history) => [...history, activeTable.name]);
    setActiveTableName(tableName);
  };
  const goBack = () => {
    setTableHistory((history) => {
      const previous = history.at(-1);
      if (previous) setActiveTableName(previous);
      return history.slice(0, -1);
    });
  };
  useEffect(() => {
    capture("solution_page_viewed", data, {
      referrer: document.referrer || "direct",
      device_type: window.innerWidth < 768 ? "mobile" : "desktop",
    });
  }, [data]);

  const schemaJson = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaJson) }}
      />
      <section className="mx-auto max-w-[1440px] px-5 pb-20 pt-16 md:px-10 lg:px-20 lg:pb-24 lg:pt-20">
        <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
          Solutions / {data.label}
        </p>
        <div className="mt-6 flex flex-col gap-10">
          <div>
            <h1 className="max-w-[960px] text-balance text-[clamp(2.75rem,6vw,5.5rem)] font-medium leading-[1.02] tracking-[-0.06em]">
              {data.h1}
            </h1>
            <p className="mt-6 max-w-[680px] text-lg leading-7 text-muted-foreground">
              {data.description}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button asChild size="marketing">
                <Link
                  onClick={() =>
                    capture("solution_primary_cta_clicked", data, {
                      cta_location: "hero",
                    })
                  }
                  href={`/getting-started/create-account?source=${data.slug}`}
                >
                  {data.primaryCta} ↗
                </Link>
              </Button>
              <Button
                asChild
                variant="link"
                size="marketing-link"
                className="text-foreground"
              >
                <Link href={demoHrefForSource(data.slug)}>
                  Try the demo workspace ↗
                </Link>
              </Button>
            </div>
          </div>
          <ul className="flex flex-wrap gap-x-8 gap-y-3 border-t pt-7 text-sm text-muted-foreground">
            {[
              "Reusable relational schema",
              "Dashboard or API workflow",
              "SQLite, JSON, CSV, or XML export",
            ].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <span aria-hidden="true" className="text-primary">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          {data.videoSrc ? (
            <div className="aspect-[48/25] overflow-hidden rounded-md border bg-card shadow-sm">
              <video
                className="h-full w-full object-contain"
                src={data.videoSrc}
                aria-label={data.videoTitle}
                poster={data.videoPoster}
                autoPlay
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
            </div>
          ) : (
            <div className="flex aspect-[16/7] min-h-[280px] flex-col justify-center rounded-md border bg-secondary/65 p-8 lg:p-12">
              <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
                Product video coming soon
              </p>
              <h2 className="mt-4 text-[clamp(2rem,3vw,2.5rem)] font-medium leading-[1.07] tracking-[-0.035em]">
                {data.videoTitle}
              </h2>
              <p className="mt-3 max-w-2xl leading-7 text-muted-foreground">
                {data.videoDescription}
              </p>
            </div>
          )}
        </div>
      </section>

      {data.guides && data.guides.length > 0 && (
        <section className="border-t bg-secondary/40">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:gap-24 lg:px-20 lg:py-28">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
                Step-by-step tutorial
              </p>
              <h2 className="mt-5 text-[clamp(2.5rem,4vw,3.5rem)] font-medium leading-[1.07] tracking-[-0.05em]">
                Build the complete workflow.
              </h2>
            </div>
            <div>
              {data.guides.map((guide, index) => (
                <article
                  key={guide.href}
                  className="grid gap-5 border-t py-8 sm:grid-cols-[54px_1fr]"
                >
                  <span className="font-mono text-sm text-primary">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-[28px] leading-9 tracking-[-0.035em]">
                      {guide.title}
                    </h3>
                    <p className="mt-3 max-w-[620px] text-[17px] leading-7 text-muted-foreground">
                      {guide.description}
                    </p>
                    <Link
                      onClick={() =>
                        capture("solution_guide_clicked", data, {
                          guide_href: guide.href,
                        })
                      }
                      href={guide.href}
                      className="mt-6 inline-block text-sm text-primary hover:underline"
                    >
                      Read article ↗
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 lg:px-20 lg:py-28">
        <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
          Transformation
        </p>
        <h2 className="mt-5 font-mono text-[clamp(1.5rem,2.6vw,2.25rem)] font-medium leading-[1.2] tracking-[-0.03em]">
          {data.transformation}
        </h2>
        <div className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(22rem,.85fr)]">
            <div className="min-w-0">
              <div className="flex items-end justify-between gap-4">
                <h3 className="text-xs font-medium uppercase tracking-[0.14em] text-primary">
                  Relational output
                </h3>
                <p className="text-xs text-muted-foreground">
                  Select a relationship to explore →
                </p>
              </div>
              <section
                id={tableId(activeTable.name)}
                className="mt-4 overflow-hidden rounded-md border bg-card shadow-sm"
              >
                <div className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b bg-secondary/40 px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    {tableHistory.length > 0 && (
                      <button
                        type="button"
                        onClick={goBack}
                        className="rounded-sm border px-2 py-1 font-mono text-[11px] uppercase tracking-[0.08em] transition-colors hover:border-primary hover:text-primary focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        ← Back
                      </button>
                    )}
                    <p className="font-mono text-xs text-foreground">
                      {activeTable.name}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {childTables.map((child) => (
                      <button
                        type="button"
                        key={child.name}
                        onClick={() => openTable(child.name)}
                        className="rounded-sm border px-2 py-1 font-mono text-[11px] text-primary transition-colors hover:border-primary hover:bg-primary/5 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none"
                      >
                        {child.name} ↓
                      </button>
                    ))}
                  </div>
                </div>
                <ScrollArea
                  type="always"
                  scrollbarOrientation="both"
                  className="h-[19rem] w-full pb-2"
                >
                  <table className="w-full min-w-[88rem] table-fixed text-left">
                    <thead className="bg-secondary/40 font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                      <tr>
                        {activeColumns.map((column) => (
                          <th
                            key={column}
                            className="border-b border-r px-3 py-2 font-medium whitespace-nowrap last:border-r-0"
                          >
                            {relatedTable(column, data.schema) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  openTable(
                                    relatedTable(column, data.schema)!.name,
                                  )
                                }
                                className="text-primary underline decoration-primary/40 underline-offset-4"
                              >
                                {column} ↗
                              </button>
                            ) : (
                              column
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from(
                        { length: SAMPLE_ROW_COUNT },
                        (_, rowIndex) => (
                          <tr
                            key={rowIndex}
                            role="button"
                            tabIndex={0}
                            aria-label={`Display source PDF ${rowIndex + 1}`}
                            aria-pressed={selectedRowIndex === rowIndex}
                            onClick={() => setSelectedRowIndex(rowIndex)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setSelectedRowIndex(rowIndex);
                              }
                            }}
                            className={`${rowIndex % 2 === 0 ? "bg-background" : "bg-muted/15"} cursor-pointer outline-none transition-colors hover:bg-primary/5 focus-visible:bg-primary/10 ${selectedRowIndex === rowIndex ? "bg-primary/10" : ""}`}
                          >
                            {activeColumns.map((column) => (
                              <td
                                key={column}
                                className="border-b border-r px-3 py-2.5 font-mono text-[11px] text-muted-foreground whitespace-nowrap last:border-r-0"
                              >
                                {sampleCell(column, activeTable.name, rowIndex)}
                              </td>
                            ))}
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </ScrollArea>
              </section>
              <p className="mt-3 text-xs text-muted-foreground">
                Relationship: {data.relationship}
              </p>
            </div>
            <SampleDocument data={data} rowIndex={selectedRowIndex} />
        </div>
      </section>

      <section className="border-y bg-secondary/65">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:px-10 lg:grid-cols-2 lg:gap-24 lg:px-20 lg:py-20">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
              Why PDFParse
            </p>
            <div className="mt-8">
              {data.benefits.map((benefit, index) => (
                <div
                  key={benefit}
                  className="grid gap-5 border-t py-8 sm:grid-cols-[54px_1fr]"
                >
                  <span className="font-mono text-sm text-primary">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="text-[17px] leading-7">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
              Edge cases
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {data.edgeCases.map((edge) => (
                <p
                  key={edge}
                  className="rounded-md border bg-card p-4 text-sm leading-6 text-muted-foreground"
                >
                  {edge}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 lg:grid-cols-[minmax(300px,0.72fr)_minmax(0,1.28fr)] lg:gap-24 lg:px-20 lg:py-28">
        <h2 className="text-[clamp(2.5rem,4vw,3.5rem)] font-medium leading-[1.07] tracking-[-0.05em]">
          Common questions.
        </h2>
        <div>
          {data.faqs.map((faq) => (
            <details
              key={faq.question}
              onToggle={(event) => {
                if ((event.target as HTMLDetailsElement).open)
                  capture("solution_faq_opened", data, {
                    question: faq.question,
                  });
              }}
              className="group border-t py-6"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-6 text-[17px] leading-7 marker:content-none [&::-webkit-details-marker]:hidden">
                {faq.question}
                <span
                  aria-hidden="true"
                  className="shrink-0 text-primary transition-transform group-open:rotate-45"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 max-w-[620px] leading-7 text-muted-foreground">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>
      </section>

      <section id="api" className="border-t bg-secondary/40">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-10 lg:grid-cols-[minmax(300px,0.8fr)_minmax(0,1.2fr)] lg:gap-24 lg:px-20 lg:py-28">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.14em] text-primary">
              Developer integration
            </p>
            <h2 className="mt-5 text-[clamp(2.5rem,4vw,3.5rem)] font-medium leading-[1.07] tracking-[-0.05em]">
              Automate from your code.
            </h2>
            <p className="mt-6 leading-7 text-muted-foreground">
              Once the dashboard workflow fits your documents, submit uploaded
              files with the same schema and retrieve the completed records
              through the API.
            </p>
            <Link
              onClick={() =>
                capture("solution_api_docs_clicked", data, {
                  cta_location: "api",
                })
              }
              href="/docs"
              className="mt-8 inline-block text-sm text-primary hover:underline"
            >
              View API documentation ↗
            </Link>
          </div>
          <CodeBlock
            title={`POST /v1/jobs · ${data.primaryTable}`}
            keepBackground
            data-line-numbers
            className="marketing-code-block my-0 rounded-md border-border shadow-none"
            viewportProps={{
              className: "max-h-none py-5 text-[0.8125rem] leading-6",
            }}
          >
            <div
              aria-label={`API example for ${data.label}`}
              dangerouslySetInnerHTML={{ __html: highlightedApiExample }}
            />
          </CodeBlock>
        </div>
      </section>
      <MossClosingCta
        title={data.finalCta}
        href={`/getting-started/create-account?source=${data.slug}-final`}
        onClick={() =>
          capture("solution_primary_cta_clicked", data, {
            cta_location: "final",
          })
        }
      />
    </>
  );
}
